// CommentsThread.js — Internal team comments per applicant
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import './CommentsThread.css';

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatTime(dt) {
  if (!dt) return '';
  try {
    const d = new Date(dt);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

export default function CommentsThread({ applicantId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [author, setAuthor] = useState(() => localStorage.getItem('crm_comment_author') || '');
  const [sending, setSending] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/comments`);
      if (res.ok) setComments(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [applicantId]);

  useEffect(() => { if (applicantId) load(); }, [applicantId, load]);

  // Scroll to bottom when new comments arrive
  useEffect(() => {
    if (!loading && comments.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments, loading]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    if (author) localStorage.setItem('crm_comment_author', author);
    setSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/applicants/${applicantId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: newComment.trim(), created_by: author.trim() || 'Anonymous' }),
      });
      if (res.ok) {
        const data = await res.json();
        setComments((prev) => [
          ...prev,
          {
            id: data.id,
            comment: newComment.trim(),
            created_by: author.trim() || 'Anonymous',
            created_at: new Date().toISOString(),
          },
        ]);
        setNewComment('');
      }
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/comments/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setComments((prev) => prev.filter((c) => c.id !== id));
        setDeleteConfirm(null);
      }
    } catch (e) { console.error(e); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(e);
  };

  return (
    <div className="comments-thread">
      <div className="comments-header">
        <h3 className="comments-title">Internal Comments</h3>
        <span className="comments-count">{comments.length}</span>
        <span className="comments-hint">Visible to team only</span>
      </div>

      <div className="comments-body">
        {loading ? (
          <div className="comments-empty">Loading…</div>
        ) : comments.length === 0 ? (
          <div className="comments-empty">
            No comments yet. Use this thread for internal team notes and discussion.
          </div>
        ) : (
          <div className="comments-list">
            {comments.map((c) => (
              <div key={c.id} className="comment-bubble-row">
                <div
                  className="comment-avatar"
                  title={c.created_by || 'Anonymous'}
                >
                  {getInitials(c.created_by)}
                </div>
                <div className="comment-content">
                  <div className="comment-meta-row">
                    <span className="comment-author">{c.created_by || 'Anonymous'}</span>
                    <span className="comment-time">{formatTime(c.created_at)}</span>
                    <button
                      className="comment-delete-btn"
                      title="Delete"
                      onClick={() => setDeleteConfirm(deleteConfirm === c.id ? null : c.id)}
                    >
                      ×
                    </button>
                  </div>
                  <div className="comment-text">{c.comment}</div>
                  {deleteConfirm === c.id && (
                    <div className="comment-delete-confirm">
                      <span>Delete this comment?</span>
                      <button className="comment-confirm-btn" onClick={() => handleDelete(c.id)}>Delete</button>
                      <button className="comment-cancel-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Compose */}
      <form className="comments-compose" onSubmit={handleSend}>
        <input
          className="comments-author-input"
          type="text"
          placeholder="Your name"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
        />
        <div className="comments-textarea-row">
          <textarea
            className="comments-textarea"
            placeholder="Write a comment… (Ctrl+Enter to send)"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
          />
          <button
            type="submit"
            className="comments-send-btn"
            disabled={sending || !newComment.trim()}
            title="Send (Ctrl+Enter)"
          >
            {sending ? '…' : '↑'}
          </button>
        </div>
      </form>
    </div>
  );
}
